import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Logo, SkeletonBlock, cx } from "@unizpay/ui";
import { Fuel, LogOut, UserRound } from "lucide-react";
import { Link, NavLink, Outlet, useNavigate } from "react-router";
import { ROLE_LABEL, api } from "./lib/api";

/** Sections of the panel, in the sidebar. */
const NAV = [
  { to: "/", label: "Relayers", icon: Fuel },
  { to: "/account", label: "Account", icon: UserRound },
];

/** Signed-in shell: top bar with the admin and sign-out, sidebar, and the page. */
export function Layout() {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 60_000 });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: api.logout,
    onSettled: () => {
      queryClient.clear();
      void navigate("/login", { replace: true });
    },
  });

  return (
    <div className="min-h-dvh bg-bg">
      <header className="sticky top-0 z-10 border-b border-line bg-surface">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 sm:px-6">
          <Logo className="h-6" />
          <span className="rounded-md bg-fg px-1.5 py-0.5 text-[0.6875rem] font-semibold tracking-wide text-surface uppercase">
            Admin
          </span>
          <div className="ml-auto flex items-center gap-3">
            {me.data && (
              <Link
                to="/account"
                className="hidden rounded-md px-2 py-1 text-right text-[0.8125rem] leading-tight hover:bg-subtle sm:block"
              >
                <span className="block font-medium text-fg">{me.data.name ?? me.data.email}</span>
                <span className="block text-xs text-fg-muted">{ROLE_LABEL[me.data.role]}</span>
              </Link>
            )}
            <Button variant="secondary" size="sm" onClick={() => logout.mutate()} loading={logout.isPending}>
              <LogOut aria-hidden="true" /> Sign out
            </Button>
          </div>
        </div>
      </header>
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 md:flex-row">
        <nav aria-label="Admin sections" className="shrink-0 md:w-44">
          <ul className="flex gap-1 md:flex-col">
            {NAV.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end
                  className={({ isActive }) =>
                    cx(
                      "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium",
                      isActive
                        ? "bg-surface text-fg shadow-xs ring-1 ring-line"
                        : "text-fg-muted hover:text-fg",
                    )
                  }
                >
                  <Icon aria-hidden="true" className="size-4" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <main className="min-w-0 flex-1">{me.data ? <Outlet /> : <SkeletonBlock lines={6} />}</main>
      </div>
    </div>
  );
}
